// src/pages/Platform/numberLifecycleRules.ts — the decisions behind the Number details
// drawer and the Add / Allot / Settings dialogs, kept out of the components so they can be
// tested. The server decides everything that matters; these only shape what is shown.
import type { SwitchPushPlan } from 'api/services/switchPush.service';
import type {
  FlowInfo, LifecyclePush, NumberSettings, SettingsChange, SettingsValues, WebhookView,
} from 'api/services/numberLifecycle.service';

/** A pasted block of numbers: one per line, or separated by commas / semicolons. Blanks dropped, order kept. */
export const parseNumberList = (text: string): string[] => {
  const out: string[] = [];
  for (const raw of (text || '').split(/[\n,;]+/)) {
    const t = raw.trim();
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
};

/** "TEEOFF / Atamantan_304". */
export const flowKey = (f: { operatorId?: string | null; serviceId?: string | null } | null | undefined): string =>
  f && f.operatorId && f.serviceId ? `${f.operatorId} / ${f.serviceId}` : '—';

/** The option text in the flow picker: key, recording folder, how many DIDs use it. */
export const flowOption = (f: FlowInfo): string => {
  const folder = f.recordingFolders.length ? ` · recordings → ${f.recordingFolders.join(', ')}` : ' · recordings → (unknown)';
  const dids = ` · ${f.dids.length} DID${f.dids.length === 1 ? '' : 's'}`;
  const empty = f.stateCount === 0 ? ' · NO STATES' : '';
  return `${flowKey(f)}${folder}${dids}${empty}`;
};

/**
 * Flows worth offering: those with states (a flow with none would not route), the Free flow
 * last (it is parking, never a holder's IVR), filtered by a search over key, folder and DIDs.
 */
export const pickableFlows = (flows: FlowInfo[], query = ''): FlowInfo[] => {
  const q = query.trim().toLowerCase();
  return flows
    .filter(f => f.stateCount > 0)
    .filter(f => !q || [f.operatorId, f.serviceId, ...f.recordingFolders, ...f.dids.map(d => d.cliNumber),
      ...f.dids.map(d => d.businessId || '')].some(v => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => Number(isFreeFlow(a)) - Number(isFreeFlow(b)) || flowKey(a).localeCompare(flowKey(b)));
};

export const isFreeFlow = (f: { serviceId?: string | null }): boolean => (f.serviceId || '').toLowerCase() === 'free';

/** The plan is blocked only because the label is not billed: the one block a person can fix here. */
export const unbilledLabel = (p: SwitchPushPlan | null | undefined): string | null => {
  if (!p || p.action !== 'BLOCKED') return null;
  const m = /Label (\S+) would be unbilled/.exec(p.reason || '');
  return m ? m[1] : null;
};

/** One line for what the switch did after allot / release. */
export const pushOutcomeText = (p: LifecyclePush | null | undefined): string => {
  if (!p) return 'The switch was not asked.';
  switch (p.outcome) {
    case 'OK': return `Switch updated${p.pushId ? ` (push #${p.pushId})` : ''}${p.cacheRefreshed === false ? '; cache refresh failed, the switch catches up within 60 s' : ''}.`;
    case 'NO_CHANGE': return 'Switch already correct; nothing written.';
    case 'PREVIEW': return 'Preview mode: the switch was NOT changed. The plan is recorded in the switch history.';
    case 'OFF': return 'Switch push is off: the switch was not read or changed.';
    case 'CONFLICT': return 'The switch changed after the preview; nothing was written. Push again from Number details.';
    case 'BLOCKED': return `Switch NOT updated: ${p.reason || 'blocked'}`;
    case 'FAILED': return `Switch write FAILED: ${p.reason || 'unknown error'}`;
    default: return String(p.outcome);
  }
};

/** Pill class for an outcome. */
export const outcomePill = (o: LifecyclePush['outcome'] | undefined): string =>
  o === 'OK' || o === 'NO_CHANGE' ? 'tl-pill tl-pill-success'
    : o === 'BLOCKED' || o === 'FAILED' || o === 'CONFLICT' ? 'tl-pill tl-pill-coral'
      : 'tl-pill tl-pill-amber';

/** Why Allot cannot be pressed yet, or null. */
export const allotBlockReason = (opts: {
  organizationId: string; previewed: boolean; refusal?: string | null; force: boolean; needsForce: boolean;
  push: boolean; mode?: string | null;
}): string | null => {
  if (!opts.organizationId) return 'Choose an organization.';
  if (!opts.previewed) return 'Preview first.';
  if (opts.needsForce && !opts.force) return opts.refusal || 'The current holder is inside its lock-in: tick "force".';
  return null;
};

/** The masked webhook as one line. */
export const webhookSummary = (w: WebhookView | null | undefined): string => {
  if (!w || !w.set) return 'Not set: call events go to the switch-wide receiver.';
  if (w.invalid) return 'Set, but not valid JSON: voiceflow ignores it.';
  const on = w.enabled === false || w.enabled === 'false' ? 'OFF' : 'on';
  const where = w.url ? w.url : 'the switch-wide receiver';
  return `Events ${on} → ${where}${w.authTokenSet ? ' · token set' : ''}${w.secretSet ? ' · signed' : ''}`;
};

export const webhookIsOn = (w: WebhookView | null | undefined): boolean =>
  !(w && (w.enabled === false || w.enabled === 'false'));

/** Draft state of the Settings tab, compared with what the switch holds. */
export interface SettingsDraft {
  dialingCode: string;
  transcribeName: string;
  defaultLanguage: string;
  webhookEnabled: boolean;
  webhookUrl: string;
  webhookAuthToken: string;
}

export const draftFrom = (s: NumberSettings | null | undefined): SettingsDraft => {
  const v: SettingsValues = s?.values || {};
  return {
    dialingCode: v.dialing_code || '',
    transcribeName: v.transcribe_name || '',
    defaultLanguage: v.default_language || '',
    webhookEnabled: webhookIsOn(v.webhook_config),
    webhookUrl: v.webhook_config?.url || '',
    webhookAuthToken: '',
  };
};

/**
 * Only what changed, so the server's guard covers only the columns being written. The URL is
 * sent only when edited: the shown URL is masked, and sending it back would overwrite the real one.
 */
export const changeFrom = (s: NumberSettings | null | undefined, d: SettingsDraft): SettingsChange => {
  const base = draftFrom(s);
  const c: SettingsChange = {};
  if (d.dialingCode !== base.dialingCode) c.dialingCode = d.dialingCode;
  if (d.transcribeName !== base.transcribeName) c.transcribeName = d.transcribeName;
  if (d.defaultLanguage !== base.defaultLanguage) c.defaultLanguage = d.defaultLanguage;
  if (d.webhookEnabled !== base.webhookEnabled) c.webhookEnabled = d.webhookEnabled;
  if (d.webhookUrl !== base.webhookUrl) c.webhookUrl = d.webhookUrl;
  if (d.webhookAuthToken) c.webhookAuthToken = d.webhookAuthToken;
  return c;
};

export const isEmptyChange = (c: SettingsChange): boolean => Object.keys(c).length === 0;
